import io from 'socket.io-client';
import { checkOrderStatus as originalCheckOrderStatus } from './requests';
import { OrderStatus } from '../constants/enums/orderStatus';
import clientLogger from '../../utils/clientLogger';

// Determine the correct base URL based on the current domain
const determineBaseUrl = () => {
  const currentDomain = window.location.hostname;

  if (currentDomain.includes('test.blends.co.il')) {
    return 'https://test.blends.co.il/api';
  } else if (currentDomain.includes('staging.blends.co.il')) {
    return 'https://staging.blends.co.il/api';
  } else if (currentDomain.includes('blends.co.il')) {
    return 'https://blends.co.il/api';
  }

  // Default to the environment variable if domain matching fails
  return process.env.REACT_APP_BASE_URL;
};

const BASE_URL = determineBaseUrl();

// Socket.IO reads a URL path as the NAMESPACE: io("https://blends.co.il/api")
// asked for namespace "/api", which the server never defines, so every socket
// got "Invalid namespace" and the client gave up for good (prod + test logs: 0
// "New client connected" lines, every handshake answered with the 38-byte
// error). The server listens on the default namespace at /socket.io/ of the
// same origin, so connect to the origin only.
const SOCKET_URL = (() => {
  try {
    return new URL(BASE_URL || '/', window.location.href).origin;
  } catch (e) {
    return undefined; // socket.io-client falls back to window.location
  }
})();

// Listeners of a checkout whose orderId was not in localStorage yet when its
// modal mounted (desktop Bit opened its modal before gama-createorder).
const PENDING_ORDER_KEY = '';

let socket = null;
let statusListeners = {};
let connectionStatus = 'disconnected';
let pollingInterval = null;
let paidOrderId = null; // PAID already handed to the listeners for this order
let roomOrderId = null; // order room joined on the current socket connection

// One checkout per tab: whoever is listening is listening for the order in
// localStorage NOW. Resolved at dispatch time, not at subscribe time.
const currentListeners = () => [].concat(...Object.values(statusListeners));

// Hand a status to the checkout's listeners. PAID goes out once per order —
// room event, global event and the poll can all report it.
const notifyListeners = (orderId, response) => {
  if (response?.data?.orderStatus === OrderStatus.PAID) {
    if (paidOrderId === orderId) {
      return;
    }
    paidOrderId = orderId;
  }
  currentListeners().forEach(callback => callback(response));
};

// Debugging function for consistent logging
const debugLog = (message, data = {}) => {
  clientLogger.debug(`[Socket] ${message}`, data);
};

// Enhanced subscription with retry logic
const ensureSubscription = (orderId) => {
  if (!orderId) {
    console.error('ensureSubscription: No orderId provided');
    return;
  }

  const attemptSubscription = () => {
    if (socket && socket.connected) {
      socket.emit('subscribeToOrder', orderId);
      roomOrderId = orderId;
      clientLogger.debug(`Subscribed to order ${orderId}`);
      return true;
    }
    return false;
  };

  // Try immediate subscription
  if (!attemptSubscription()) {
    clientLogger.debug(`Socket not connected, waiting for connection to subscribe to order ${orderId}`);

    // Set up retry on connection
    const connectHandler = () => {
      attemptSubscription();
      socket.off('connect', connectHandler); // Remove handler after successful subscription
    };

    socket.on('connect', connectHandler);

    // Also try again after a short delay
    setTimeout(() => {
      if (!attemptSubscription()) {
        clientLogger.warn(`Still unable to subscribe to order ${orderId} after delay`);
      }
    }, 1000);
  }
};

// Initialize socket connection
export const initializeSocket = () => {
  if (socket) {
    clientLogger.debug("Socket already initialized");
    return;
  }

  clientLogger.debug(`Initializing socket connection`);

  try {
    socket = io(SOCKET_URL);

    // Fires on the first connect AND on every reconnect (new socket id, rooms
    // gone). Polling keeps running as the backup: a phone tab frozen while the
    // customer is in the Bit app misses the room event entirely.
    socket.on('connect', () => {
      clientLogger.debug('Socket connected', { socketId: socket.id });

      connectionStatus = 'connected';
      roomOrderId = null; // a (re)connected socket is in no rooms

      // Re-subscribe to any active orders
      const activeOrders = Object.keys(statusListeners).filter(Boolean);
      const currentOrderId = localStorage.getItem("orderId");

      // Always subscribe to current order if exists
      if (currentOrderId) {
        ensureSubscription(currentOrderId);
      }

      if (activeOrders.length > 0) {
        activeOrders.forEach(orderId => {
          if (orderId !== currentOrderId) { // Avoid duplicate subscription
            socket.emit('subscribeToOrder', orderId);
          }
        });
      }

      // An update emitted while we were disconnected is lost — ask once.
      if (currentListeners().length > 0 && currentOrderId !== paidOrderId) {
        pollOnce(false);
      }
    });

    socket.on('connect_error', (error) => {
      clientLogger.debug('Socket connection error', { error: error.message });

      connectionStatus = 'disconnected';
      startPolling(); // Start polling when connection fails
    });

    socket.on('disconnect', (reason) => {
      clientLogger.debug('Socket disconnected', { reason });

      connectionStatus = 'disconnected';
      startPolling(); // Start polling when disconnected
    });

  } catch (error) {
    console.error("Error initializing socket:", error.message);
    connectionStatus = 'disconnected';
    startPolling(); // Start polling when initialization fails
  }

  // Listen for subscription confirmation
  socket.on('subscriptionConfirmed', (data) => {
    clientLogger.debug('Subscription confirmed', { orderId: data.orderId });
  });

  // The room event (order-${orderId}) and the global broadcast carry the same
  // payload; both go through one handler and reach the listeners once.
  const onSocketStatus = (source) => (data) => {
    const orderId = localStorage.getItem("orderId");

    clientLogger.debug(`Status update received (${source})`, { status: data.status, orderId });

    // Nothing to do once the checkout left localStorage; the global broadcast
    // carries every customer's orders, so it must match ours.
    if (!orderId || (source === 'global' && String(data.orderId) !== orderId)) {
      return;
    }

    if (data.status === OrderStatus.PAID) {
      window.successfulPaymentReceived = true;
      sessionStorage.setItem('successfulPaymentReceived', 'true');
      isPaymentActive = false;
      stopPolling(); // Stop polling on PAID

      // A payment modal is listening: it closes, clears the checkout from
      // localStorage and navigates. Only a page with no modal left (e.g. the
      // customer closed it and paid from the SMS) follows the server's URL.
      if (currentListeners().length === 0 && data.redirect && data.redirect.url) {
        debugLog(`Received redirect instruction to ${data.redirect.url}`);

        // Set navigation guard
        window.isNavigatingToSuccessPage = true;
        sessionStorage.setItem('isNavigatingToSuccessPage', 'true');

        window.location.href = data.redirect.url;
        return;
      }
    }

    // Stop polling on ERROR status - no point continuing
    if (data.status === OrderStatus.ERROR) {
      clientLogger.debug('Order error status received, stopping polling');
      stopPolling();
    }

    // Same shape as the /user/checkorder response the listeners already handle
    notifyListeners(orderId, { data: { status: 200, orderStatus: data.status } });
  };

  socket.on('orderStatusUpdate', onSocketStatus('room'));
  // Also listen for the global update as a fallback
  socket.on('globalOrderUpdate', onSocketStatus('global'));
};

// Polling intervals
const PAYMENT_POLL_INTERVAL = 2000; // 2 seconds during active payment
const NORMAL_POLL_INTERVAL = 5000; // 5 seconds normally
const MAX_PAYMENT_POLLS = 60; // Max 2 minutes of aggressive polling

const STALE_CHECK_MS = 8000; // a check older than this no longer blocks a new one
const RETURN_CHECK_GAP_MS = 1000; // focus + visibilitychange + pageshow fire together

let paymentPollCount = 0;
let isPaymentActive = false;
let checkInFlight = false;
let lastCheckAt = 0;

// One /user/checkorder round trip, shared by the poll interval and the
// back-on-the-page check. `fromInterval` keeps the aggressive→normal budget
// counting only real ticks.
const pollOnce = async (fromInterval = false) => {
  const orderId = localStorage.getItem("orderId");
  // GC- gift orders: /user/checkorder can't parse them (server logs "Order
  // not found" every tick) — the gift page polls /giftcard/status itself,
  // and the socket-room subscription still delivers the webhook redirect.
  if (!orderId || orderId.startsWith("GC-")) {
    return;
  }
  // Don't stack requests (resume fires the pending tick + the return events
  // at once), but never let one hung request block polling for good.
  if (checkInFlight && Date.now() - lastCheckAt < STALE_CHECK_MS) {
    return;
  }
  checkInFlight = true;
  lastCheckAt = Date.now();

  // The orderId can appear after the socket connected (order created after the
  // modal opened) — join its room as soon as we see it.
  if (socket && socket.connected && roomOrderId !== orderId) {
    ensureSubscription(orderId);
  }

  try {
    const response = await originalCheckOrderStatus();

    // Set success flag if order is paid
    if (response?.data?.orderStatus === OrderStatus.PAID) {
      window.successfulPaymentReceived = true;
      sessionStorage.setItem('successfulPaymentReceived', 'true');
      isPaymentActive = false; // Stop aggressive polling
      stopPolling();
      clientLogger.debug('Payment confirmed via polling');
    }

    // Stop polling if order has ERROR status - no point continuing
    if (response?.data?.orderStatus === OrderStatus.ERROR) {
      isPaymentActive = false;
      stopPolling();
      clientLogger.debug('Order error via polling, stopping');
    }

    // Check if we should stop aggressive polling
    if (fromInterval && isPaymentActive) {
      paymentPollCount++;
      if (paymentPollCount >= MAX_PAYMENT_POLLS) {
        clientLogger.debug('Switching from aggressive to normal polling after timeout');
        isPaymentActive = false;
        stopPolling();
        startPolling(false); // Restart with normal interval
      }
    }

    if (response) {
      notifyListeners(orderId, response);
    }
  } catch (err) {
    console.error(`Polling error for order ${orderId}:`, err.message);
  } finally {
    checkInFlight = false;
  }
};

const startPolling = (aggressive = false) => {
  if (pollingInterval) {
    return;
  }

  if (aggressive) {
    isPaymentActive = true;
    paymentPollCount = 0;
    clientLogger.debug('Starting aggressive polling for payment');
  }

  const pollInterval = isPaymentActive ? PAYMENT_POLL_INTERVAL : NORMAL_POLL_INTERVAL;

  pollingInterval = setInterval(() => {
    pollOnce(true);
  }, pollInterval);
};

// Back on the page (returned from the Bit / wallet app, tab re-shown, bfcache
// restore, network back). Mobile browsers freeze background tabs, so while the
// customer approves in Bit nothing here runs — and the socket is no help (it is
// frozen too). This is the moment the payment most likely just completed, so
// check right away instead of waiting for the next tick, and re-arm the fast
// 2 s polling in case the webhook is still a few seconds out.
const onReturnToPage = () => {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }
  const orderId = localStorage.getItem("orderId");
  if (!orderId || orderId.startsWith("GC-") || orderId === paidOrderId
      || currentListeners().length === 0) {
    return;
  }

  stopPolling();
  startPolling(true);

  if (Date.now() - lastCheckAt >= RETURN_CHECK_GAP_MS) {
    clientLogger.debug('Back on the page - checking order status now', { orderId });
    pollOnce(false);
  }
};

const RETURN_EVENTS = [
  ['visibilitychange', () => document],
  ['pageshow', () => window],
  ['focus', () => window],
  ['online', () => window],
];
let returnHandlersInstalled = false;

const installReturnHandlers = () => {
  if (returnHandlersInstalled || typeof window === 'undefined') {
    return;
  }
  RETURN_EVENTS.forEach(([type, target]) => target().addEventListener(type, onReturnToPage));
  returnHandlersInstalled = true;
};

const removeReturnHandlers = () => {
  if (!returnHandlersInstalled) {
    return;
  }
  RETURN_EVENTS.forEach(([type, target]) => target().removeEventListener(type, onReturnToPage));
  returnHandlersInstalled = false;
};

const stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

// Enhanced version of checkOrderStatus that uses WebSockets when available
export const checkOrderStatus = async () => {
  const orderId = localStorage.getItem("orderId");
  if (!orderId) {
    return null;
  }

  try {
    // Always do an initial REST call to get the current status
    const initialResponse = await originalCheckOrderStatus();

    // Set success flag if order is paid
    if (initialResponse?.data?.orderStatus === OrderStatus.PAID) {
      window.successfulPaymentReceived = true;
      sessionStorage.setItem('successfulPaymentReceived', 'true');
    }

    // Set up WebSocket subscription using enhanced subscription
    if (socket && connectionStatus === 'connected') {
      if (!statusListeners[orderId]) {
        statusListeners[orderId] = [];
        ensureSubscription(orderId);
      } else {
        ensureSubscription(orderId);
      }
    } else {
      startPolling();
    }

    return initialResponse;
  } catch (error) {
    console.error(`checkOrderStatus error for order ${orderId}:`, error.message);
    throw error;
  }
};

// Register a callback to be notified of status changes
export const subscribeToOrderStatus = (callback) => {
  // The orderId may not be in localStorage yet (desktop Bit opened its modal
  // before gama-createorder returned). Register anyway: listeners are resolved
  // when a status arrives, and polling reads the orderId on every tick.
  const orderId = localStorage.getItem("orderId");
  const key = orderId || PENDING_ORDER_KEY;

  // Initialize socket if not already done
  if (!socket) {
    initializeSocket();
  }

  // Add the callback to our listeners
  if (!statusListeners[key]) {
    statusListeners[key] = [];
  }

  statusListeners[key].push(callback);
  installReturnHandlers();

  // Poll while a checkout is listening, socket or not (the socket only adds a
  // faster path); subscribe to the room when we can.
  startPolling();
  if (orderId && connectionStatus === 'connected') {
    ensureSubscription(orderId);
  }

  // Return an unsubscribe function
  return () => {
    if (statusListeners[key]) {
      statusListeners[key] = statusListeners[key].filter(cb => cb !== callback);

      if (statusListeners[key].length === 0) {
        delete statusListeners[key];
      }
    }

    // If no more listeners, stop polling
    if (currentListeners().length === 0) {
      stopPolling();
      removeReturnHandlers();
    }
  };
};

// Function to verify payment status after redirect
export const verifyPaymentStatus = async () => {
  const orderId = localStorage.getItem("orderId");
  if (!orderId) {
    console.error("verifyPaymentStatus: No orderId found");
    return null;
  }

  clientLogger.debug(`Verifying payment status for order ${orderId}`);

  // Start aggressive polling
  startPolling(true);

  // Also make an immediate check
  try {
    const response = await originalCheckOrderStatus();
    if (response?.data?.orderStatus === OrderStatus.PAID) {
      clientLogger.debug(`Payment verified as PAID for order ${orderId}`);
      window.successfulPaymentReceived = true;
      sessionStorage.setItem('successfulPaymentReceived', 'true');
      stopPolling();
      return response;
    }
    return response;
  } catch (error) {
    console.error(`Error verifying payment status: ${error.message}`);
    // Keep aggressive polling running on error
    return null;
  }
};

// Function to start aggressive polling (exported for use in payment flows)
export const startAggressivePolling = () => {
  const orderId = localStorage.getItem("orderId");
  if (!orderId) {
    console.error("startAggressivePolling: No orderId found");
    return;
  }

  clientLogger.debug(`Starting aggressive polling for order ${orderId}`);

  // Ensure subscription
  ensureSubscription(orderId);

  // Start aggressive polling
  startPolling(true);
};
